import { useEffect, useState } from 'react';
import { Button, Card, Stack, Cluster, Text, Badge } from './ui';
import { AddCardModal } from './AddCardModal';
import { useToast } from './Toast';
import { fetchMyCards, setDefaultCard, removeMyPaymentMethod, type MemberCard } from '../lib/queries';

/**
 * The member's saved bidding cards (profile > Auction payments). Add, choose a
 * default, and remove. The default is what an auction win is charged to on
 * close; at Buy Now / when fixing a declined order the member can pick another.
 */

function brandLabel(brand: string | null): string {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function expLabel(c: MemberCard): string {
  return c.card_exp_month
    ? ` · exp ${String(c.card_exp_month).padStart(2, '0')}/${String(c.card_exp_year ?? '').slice(-2)}`
    : '';
}

export function CardBook() {
  const toast = useToast();
  const [cards, setCards] = useState<MemberCard[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    return fetchMyCards()
      .then(setCards)
      .catch(() => { /* leave as-is */ })
      .finally(() => setLoaded(true));
  }
  useEffect(() => { void load(); }, []);

  async function makeDefault(id: string) {
    setBusyId(id);
    try {
      await setDefaultCard(id);
      await load();
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not set the default card.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(card: MemberCard) {
    if (!window.confirm(`Remove ${brandLabel(card.card_brand)} ···· ${card.card_last4 ?? '••••'}?`)) return;
    setBusyId(card.id);
    try {
      await removeMyPaymentMethod(card.id);
      toast.success('Card removed.');
      await load();
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not remove the card.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="profile__section">
      <Cluster justify="between" align="center" gap="group">
        <p className="kicker">Auction payments</p>
        <Button type="button" variant="soft" size="sm" onClick={() => setShowAdd(true)}>
          Add a card
        </Button>
      </Cluster>

      <Text role="small" muted>
        The cards you can bid and pay with. Your default is charged when you win;
        you can pick another at checkout. You're only charged if you win.
      </Text>

      {!loaded ? (
        <Text role="small" muted className="mt-3">Loading…</Text>
      ) : cards.length === 0 ? (
        <Card surface="bone" pad="md" className="mt-3">
          <Text role="small" muted>
            No bidding card on file. Add one to place bids in the Sales Room.
          </Text>
        </Card>
      ) : (
        <Stack gap="group" className="mt-3">
          {cards.map((c) => (
            <Card key={c.id} bordered pad="md">
              <Cluster justify="between" align="center" gap="group" wrap>
                <Cluster gap="tight" align="center">
                  <Text role="label" as="span">
                    {brandLabel(c.card_brand)} ···· {c.card_last4 ?? '••••'}
                  </Text>
                  <Text role="small" muted as="span">{expLabel(c).replace(/^ · /, '')}</Text>
                  {c.is_default && <Badge tone="accent">Default</Badge>}
                </Cluster>
                <Cluster gap="tight" wrap>
                  {!c.is_default && (
                    <Button type="button" variant="ghost" size="sm"
                            onClick={() => makeDefault(c.id)} disabled={busyId === c.id}>
                      Make default
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="sm" tone="danger"
                          onClick={() => remove(c)} disabled={busyId === c.id}>
                    Remove
                  </Button>
                </Cluster>
              </Cluster>
            </Card>
          ))}
        </Stack>
      )}

      {showAdd && <AddCardModal directNew onClose={() => setShowAdd(false)} onLinked={() => { setShowAdd(false); void load(); }} />}
    </section>
  );
}
